import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from 'hooks';
import { get, set } from '@totalsoft/change-tracking-react';
import { Grid } from '@mui/material';
import { Autocomplete } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { emptyArray } from 'constants/defaultValues';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';

const RcaComponent = ({
  insuranceDataLens,
  financingTypes,
  activityTypes,
  periods,
  validationInsuranceData,
  onFinancingTypeChange,
  showActivityField,
  readOnly
}) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditInsuranceData = useAuthorization(AuthorizationCodes.hasRightToEditInsuranceData, documentTypeId, documentStateId);
  const { t } = useTranslation();
  const optionEqualsValueAutocomplete = useCallback((option, value) => option.id === value, []);

  return (
    <Grid container spacing={3}>
      <Grid item container spacing={3}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            id='Rca_FinancingType'
            label={t('Offer.Edit.InsuranceData.FinancingType')}
            fullWidth
            required
            isSearchable={true}
            isClearable={false}
            valueKey='name'
            labelKey='name'
            value={insuranceDataLens.financingType |> get}
            onChange={onFinancingTypeChange}
            options={financingTypes || emptyArray}
            disabled={readOnly({
              controlName: 'InsuranceData.FinancingType',
              hasAuthorization: hasRightToEditInsuranceData
            })}
            simpleValue={true}
            error={!isValid(validationInsuranceData.financingType)}
            helperText={getErrors(validationInsuranceData.financingType)}
          />
        </Grid>
        {showActivityField && (
          <Grid item xs={12} md={6}>
            <Autocomplete
              id='Rca_ActivityType'
              label={t('Offer.Edit.InsuranceData.ActivityType')}
              fullWidth
              required
              isSearchable={true}
              isClearable={false}
              valueKey='id'
              labelKey='name'
              value={insuranceDataLens.activityTypeId |> get}
              options={activityTypes}
              disabled={true}
              simpleValue={true}
              onChange={() => {}}
            />
          </Grid>
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          id='Rca_MonthNo'
          label={t('Offer.Edit.InsuranceData.MonthNo')}
          fullWidth
          required
          isSearchable={true}
          isClearable={false}
          valueKey='id'
          labelKey='id'
          value={insuranceDataLens.monthNo |> get}
          onChange={value => (insuranceDataLens.monthNo |> set)(value.id)}
          options={periods ? periods : emptyArray}
          disabled={readOnly({
            controlName: 'InsuranceData.MonthNo',
            hasAuthorization: hasRightToEditInsuranceData
          })}
          isOptionEqualToValue={optionEqualsValueAutocomplete}
          error={!isValid(validationInsuranceData.monthNo)}
          helperText={getErrors(validationInsuranceData.monthNo)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <DateTime
          id='Rca_StartDate'
          label={t('Offer.Edit.InsuranceData.StartDate')}
          onChange={insuranceDataLens.startDate |> set}
          value={insuranceDataLens.startDate |> get}
          isValidDate={date => date.isSameOrAfter(new Date(), 'day')}
          timeFormat={false}
          fullWidth
          required
          disabled={readOnly({
            controlName: 'InsuranceData.StartDate',
            hasAuthorization: hasRightToEditInsuranceData
          })}
          error={!isValid(validationInsuranceData.startDate)}
          helperText={getErrors(validationInsuranceData.startDate)}
        />
      </Grid>
    </Grid>
  );
};

RcaComponent.propTypes = {
  insuranceDataLens: PropTypes.object.isRequired,
  financingTypes: PropTypes.array,
  activityTypes: PropTypes.array,
  periods: PropTypes.array,
  validationInsuranceData: PropTypes.object,
  onFinancingTypeChange: PropTypes.func,
  showActivityField: PropTypes.bool,
  readOnly: PropTypes.func.isRequired
};

export default RcaComponent;
